import React, { useCallback, type JSX } from "react";
import { Button, MaybeElement } from "@blueprintjs/core";
import { texts } from "modules/common/texts";
import { ItemRenderer, ItemPredicate, Select, SelectProps } from "@blueprintjs/select";
import { MenuItem } from "@blueprintjs/core";
import { DictionaryItemType } from "modules/common/models/entity";
import classnames from "classnames";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";

export const prevent = (e: React.MouseEvent) => e.stopPropagation();

export function SelectFactory<TItem>(matchWidth = true) {
  const Papa = Select.ofType<TItem>();

  // matchTargetWidth в Blueprint 5 переехал из пропов Select в popoverProps
  const result: React.FC<SelectProps<TItem> & { matchTargetWidth?: boolean }> = ({
    popoverProps,
    matchTargetWidth,
    ...rest
  }) => {
    const popProps = {
      matchTargetWidth: matchTargetWidth ?? matchWidth,
      ...popoverProps,
    };

    return <Papa {...rest} popoverProps={popProps} className={classnames("select", rest.className)} />;
  };

  return result;
}

export const SimpleSelect = SelectFactory<SelectItem>(true);

export interface RendererProps {
  option: DictionaryItemType | null | undefined;
  disabled?: boolean;
  icon: JSX.Element;
  text: string;
  style?: React.CSSProperties;
}

export interface SelectedOptionProps {
  option: DictionaryItemType | null | undefined;
  onClear?: () => void;
  what?: string;
  empty?: boolean;
  disabled?: boolean;
  render?: (props: RendererProps) => any;
  style?: React.CSSProperties;
}

const DefaultRenderer = ({ disabled, icon, text, style }: RendererProps) => {
  return (
    <Button disabled={disabled} style={style} fill={true} rightIcon={icon} className="selected-option" text={text} />
  );
};

export const DefaultSelectedOption = ({
  option,
  disabled,
  onClear,
  what,
  empty,
  render,
  style,
}: SelectedOptionProps) => {
  const clear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      !disabled && onClear && onClear();
    },
    [onClear, disabled]
  );

  // Обёртка — span, а не фрагмент: Blueprint 6 клонирует иконку кнопки и дописывает
  // ей className, а у фрагмента такого пропа нет — React 19 на это предупреждает.
  // Зазор 8px — тот, что Blueprint даёт соседним детям кнопки через margin-right:
  // пока иконки лежали во фрагменте, крестик получал его сам.
  const icon =
    onClear && option ? (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <GeneralIcon type="general-cross-small" onClick={clear} style={{ color: "#E31818" }} />
        <GeneralIcon type="general-chevron-down" />
      </span>
    ) : (
      <GeneralIcon type="general-chevron-down" />
    );

  const nothing = what === undefined ? " " : what;
  const text = empty ? texts.noData : option ? option.label || nothing : nothing;

  return (render || DefaultRenderer)({
    icon,
    option,
    text,
    disabled,
    style,
  });
};

export const renderSingleOptionFactory = (
  leftElement: (item: SelectItem) => MaybeElement
): ItemRenderer<SelectItem> => {
  return (item, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }

    const text = item.label || texts.undefined;
    const style: React.CSSProperties | undefined = item.color
      ? {
          backgroundColor: item.color,
        }
      : undefined;

    return (
      <MenuItem
        active={modifiers.active}
        disabled={modifiers.disabled}
        key={item.id}
        onClick={handleClick}
        text={text}
        className={item.removed ? "removed" : ""}
        icon={leftElement(item)}
        style={style ? style : item.width ? { width: item.width } : {}}
      />
    );
  };
};

export const renderSingleOption = renderSingleOptionFactory(() => null);

export const filterItemPredicate: ItemPredicate<any> = (query, item, _index, exactMatch) => {
  const normalizedTitle = item.label.toLowerCase();
  const normalizedQuery = query.toLowerCase().trim();

  if (exactMatch) {
    return normalizedTitle === normalizedQuery;
  } else {
    return normalizedTitle.indexOf(normalizedQuery) >= 0;
  }
};

export function buildOptions(
  include: DictionaryItemType | any | null,
  items: DictionaryItemType[] | any,
  optional = false
) {
  const result: TStringMap<SelectItem> = optional
    ? {
        "": { id: "", label: texts.undefined },
      }
    : {};

  items.forEach((item: any) => {
    result[item.id] = item;
  });

  if (include && !result[include.id]) {
    result[include.id] = include;
  }

  return result;
}

/**
 * Выпадающий список по ширине поля. Раньше ширину задавал самописный модификатор
 * popper 1 — в Blueprint 5 у popover'а для этого есть matchTargetWidth, и он же
 * стоит у Select. Сам список тянется до ширины контейнера классом
 * planr-match-width-popover (правило в planr-inputs.scss).
 */
export const matchTargetWidthPopover = (extra: TStringMap<any> = {}): any => ({
  minimal: true,
  rootBoundary: "viewport",
  position: "bottom-left",
  matchTargetWidth: true,
  ...extra,
  popoverClassName: classnames("planr-match-width-popover", extra.popoverClassName),
});
